import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import DataTable from "../../components/DataTable";
import TdLink from "../../components/TdLink";
import LoadingTablePage from "../LoadingTablePage";
import { useAuth } from "../../providers/AuthContext";
import { getAllGridAreas } from "../../api/GridCost";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export default function GridAreasPage() {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const navigate = useNavigate();
    const [areas, setAreas] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        loadAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    const loadAreas = async () => {
        setLoaded(false);
        setLoadError(null);
        try {
            const token = await getAccessToken();
            const list = (await getAllGridAreas(token)) || [];
            setAreas(
                list.slice().sort((a, b) =>
                    (a.gridAreaCode || "").localeCompare(b.gridAreaCode || "", undefined, {
                        sensitivity: "base",
                    })
                )
            );
        } catch (err) {
            setAreas([]);
            setLoadError(err.message);
            toast.error(err.message);
        } finally {
            setLoaded(true);
        }
    };

    if (!loaded) {
        return (
            <>
                <HeaderBar />
                <div className="flex w-full min-h-0">
                    <Sidebar />
                    <main className="page">
                        <LoadingTablePage title={t("entity.gridAreas")} loading />
                    </main>
                </div>
            </>
        );
    }

    const columns = [
        {
            key: "gridAreaCode",
            header: t("common.code"),
            sortable: true,
            rawCell: true,
            getSortValue: (row) => row.gridAreaCode ?? "",
            render: (row) => (
                <TdLink to={`/grid-areas/${row.gridAreaPublicId}`}>{row.gridAreaCode}</TdLink>
            ),
        },
        {
            key: "areaName",
            header: t("common.name"),
            sortable: true,
            rawCell: true,
            getSortValue: (row) => row.areaName ?? "",
            render: (row) => (
                <TdLink to={`/grid-areas/${row.gridAreaPublicId}`}>{row.areaName}</TdLink>
            ),
        },
        {
            key: "companyName",
            header: t("common.company"),
            sortable: true,
            rawCell: true,
            getSortValue: (row) => row.companyName ?? "",
            render: (row) => (
                <TdLink to={`/grid-areas/${row.gridAreaPublicId}`}>{row.companyName}</TdLink>
            ),
        },
        {
            key: "organizationName",
            header: t("entity.organization"),
            sortable: true,
            rawCell: true,
            hidden: true,
            getSortValue: (row) => row.organizationName ?? "",
            render: (row) => (
                <TdLink to={`/grid-areas/${row.gridAreaPublicId}`}>{row.organizationName || t("common.allOrganizations")}</TdLink>
            ),
        },
    ];

    return (
        <>
            <HeaderBar />
            <div className="flex w-full min-h-0">
                <Sidebar />
                <main className="page">
                    <div className="page-content">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <h2>{t("entity.gridAreas")}</h2>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate("/grid-areas/create")}
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                {t("pages.createGridArea")}
                            </button>
                        </div>
                        {loadError && (
                            <p className="text-sm text-red-600 mb-4">
                                {t("toast.failedLoad", { entity: t("entity.gridAreas") })}: {loadError}
                            </p>
                        )}
                        <div className="rounded-md shadow-md w-full p-4">
                            <DataTable
                                rows={areas}
                                columns={columns}
                                search={search}
                                onSearchChange={setSearch}
                                searchPlaceholder={t("search.gridArea")}
                                defaultSortColumn="gridAreaCode"
                                rowKey={(row, index) => row.gridAreaPublicId || index}
                                emptyColSpan={4}
                            />
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}
